import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Heart, ShieldCheck, Search, ArrowRight, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

export default function LandingPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (user && user.role) return <Navigate to="/dashboard" replace />;

  return (
    <div data-testid="landing-page">
      {/* Hero — archival desk photo with European network map */}
      <section className="relative overflow-hidden bg-[#0B1119]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/eurofind-hero.jpg')" }}
          data-testid="hero-background"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(9,13,20,0.90) 0%, rgba(9,13,20,0.72) 40%, rgba(9,13,20,0.35) 75%, rgba(9,13,20,0.25) 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-24"
          style={{ background: "linear-gradient(180deg, rgba(9,13,20,0) 0%, rgba(9,13,20,0.55) 100%)" }}
        />
        <div className="hero-grain absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-6xl px-6 pt-24 pb-28 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-12 lg:pt-28 lg:pb-36">
          <div className="max-w-2xl">
            <img
              src="/eurofind-logo-transparent.png"
              alt="EuroFind"
              className="mb-6 w-28 drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)] md:w-32 lg:hidden"
              data-testid="landing-eurofind-logo-mobile"
            />
            <p
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 font-body text-xs uppercase tracking-[0.18em] text-[#E8EDF2] backdrop-blur-sm"
              data-testid="landing-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#D9A441]" /> {t("landing.eyebrow")}
            </p>
            <h1
              className="font-heading text-4xl font-semibold text-white sm:text-5xl lg:text-6xl text-balance"
              style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
            >
              {t("landing.hero_title")}
            </h1>
            <p
              className="mt-6 font-body text-base text-[#C9D3DC] sm:text-lg"
              style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
            >
              {t("landing.hero_subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register?role=family" data-testid="hero-family-cta">
                <Button size="lg" className="h-12 bg-[#D9A441] px-6 font-semibold text-[#1A1408] shadow-lg shadow-black/30 hover:bg-[#E5B558]">
                  <Heart size={18} className="mr-2" /> {t("landing.cta_family")}
                </Button>
              </Link>
              <Link to="/register?role=authority" data-testid="hero-authority-cta">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 border-white/40 bg-white/10 px-6 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
                >
                  <ShieldCheck size={18} className="mr-2" /> {t("landing.cta_authority")}
                </Button>
              </Link>
            </div>
          </div>
          <img
            src="/eurofind-logo-transparent.png"
            alt="EuroFind — connecting families and forensic professionals across Europe"
            className="hidden w-64 shrink-0 drop-shadow-[0_10px_40px_rgba(0,0,0,0.65)] lg:block xl:w-72"
            data-testid="landing-eurofind-logo"
          />
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-[#E2E8F0] bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px bg-[#E2E8F0] sm:grid-cols-4">
          {[
            { label: t("landing.stat.countries"), value: stats?.countries ?? "—" },
            { label: t("landing.stat.ante"), value: stats?.ante_mortem ?? "—" },
            { label: t("landing.stat.post"), value: stats?.post_mortem ?? "—" },
            { label: t("landing.stat.users"), value: stats?.users ?? "—" },
          ].map((s) => (
            <div key={s.label} className="bg-white p-8" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <div className="font-heading text-3xl font-semibold text-[#1A2024]">{s.value}</div>
              <div className="mt-1 font-body text-sm text-[#5C6873]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Positioning statement */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="mx-auto max-w-4xl px-6 py-16" data-testid="landing-positioning">
          <div className="border-l-4 border-[#4A7C59] pl-6 sm:pl-8">
            <h2 className="font-heading text-2xl font-semibold leading-snug text-[#1A2024] sm:text-3xl">
              {t("landing.pos_title")}
            </h2>
            <p className="mt-4 max-w-3xl font-body text-base leading-relaxed text-[#5C6873]">
              {t("landing.pos_body")} <em className="font-medium not-italic text-[#4A7C59]">{t("landing.pos_complement")}</em>
            </p>
          </div>
        </div>
      </section>

      {/* Trust / reassurance */}
      <section className="bg-[#F2F6F3]">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center" data-testid="landing-trust">
          <Heart size={22} className="mx-auto text-[#4A7C59]" />
          <h2 className="mt-4 font-heading text-2xl font-semibold text-[#1A2024] sm:text-3xl">{t("landing.trust_title")}</h2>
          <p className="mx-auto mt-3 max-w-2xl font-body text-base text-[#5C6873]">{t("landing.trust_body")}</p>
        </div>
      </section>

      {/* Two-side explainer */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="font-heading text-2xl font-semibold text-[#1A2024] sm:text-3xl">{t("landing.sides_title")}</h2>
        <p className="mt-2 max-w-2xl font-body text-base text-[#5C6873]">
          {t("landing.sides_subtitle")}
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-[#E2E8F0] bg-white p-8 shadow-sm transition-base hover:border-[#2E3A46]/40" data-testid="card-family">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-[#4A7C59]/10 text-[#4A7C59]">
                <Heart size={18} />
              </span>
              <h3 className="font-heading text-xl font-semibold text-[#1A2024]">{t("landing.family_card_title")}</h3>
            </div>
            <p className="mt-4 font-body text-sm text-[#5C6873]">
              {t("landing.family_card_body")}
            </p>
            <ul className="mt-5 space-y-2 font-body text-sm text-[#1A2024]">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#4A7C59]" /> {t("landing.family_b1")}</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#4A7C59]" /> {t("landing.family_b2")}</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#4A7C59]" /> {t("landing.family_b3")}</li>
            </ul>
            <Link to="/register?role=family" className="mt-6 inline-flex items-center gap-1 font-body text-sm font-medium text-[#2E3A46] hover:text-[#1A2024]" data-testid="card-family-link">
              {t("landing.family_link")} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="rounded-lg border border-[#E2E8F0] bg-white p-8 shadow-sm transition-base hover:border-[#2E3A46]/40" data-testid="card-authority">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-[#2E3A46]/10 text-[#2E3A46]">
                <ShieldCheck size={18} />
              </span>
              <h3 className="font-heading text-xl font-semibold text-[#1A2024]">{t("landing.authority_card_title")}</h3>
            </div>
            <p className="mt-4 font-body text-sm text-[#5C6873]">
              {t("landing.authority_card_body")}
            </p>
            <ul className="mt-5 space-y-2 font-body text-sm text-[#1A2024]">
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#2E3A46]" /> {t("landing.authority_b1")}</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#2E3A46]" /> {t("landing.authority_b2")}</li>
              <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#2E3A46]" /> {t("landing.authority_b3")}</li>
            </ul>
            <Link to="/register?role=authority" className="mt-6 inline-flex items-center gap-1 font-body text-sm font-medium text-[#2E3A46] hover:text-[#1A2024]" data-testid="card-authority-link">
              {t("landing.authority_link")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-[#E2E8F0] bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="font-heading text-2xl font-semibold text-[#1A2024] sm:text-3xl">{t("landing.how_title")}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: t("landing.step1_title"), d: t("landing.step1_desc") },
              { n: "02", t: t("landing.step2_title"), d: t("landing.step2_desc") },
              { n: "03", t: t("landing.step3_title"), d: t("landing.step3_desc") },
            ].map((s) => (
              <div key={s.n} className="rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-6" data-testid={`step-${s.n}`}>
                <div className="font-heading text-xs font-semibold tracking-[0.2em] text-[#6B7F82]">STEP {s.n}</div>
                <div className="mt-2 font-heading text-lg font-semibold text-[#1A2024]">{s.t}</div>
                <div className="mt-2 font-body text-sm text-[#5C6873]">{s.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex gap-3">
            <Link to="/browse" data-testid="hero-browse-link">
              <Button variant="outline" className="border-[#E2E8F0] bg-white text-[#2E3A46]">
                <Search size={16} className="mr-2" /> {t("landing.browse_btn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* International helping hands */}
      <section className="border-t border-[#E2E8F0] bg-[#2E3A46]">
        <div className="mx-auto max-w-6xl px-6 py-16" data-testid="landing-helpers">
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 font-heading text-xs font-semibold tracking-[0.2em] text-[#C99B4B]">
                <HeartHandshake size={15} /> {t("help.landing_eyebrow")}
              </div>
              <h2 className="mt-3 font-heading text-2xl font-semibold text-white sm:text-3xl">{t("help.landing_title")}</h2>
              <p className="mt-3 max-w-2xl font-body text-sm text-white/70">{t("help.landing_text")}</p>
            </div>
            <Link to="/contribute?tab=apply" data-testid="landing-helpers-cta">
              <Button className="h-12 bg-white px-6 text-[#2E3A46] hover:bg-white/90">
                {t("help.landing_cta")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Three ways to move EuroFind forward */}
      <section className="border-t border-black/40 bg-[#1A2024]">
        <div className="mx-auto max-w-6xl px-6 py-20" data-testid="landing-ways">
          <h2 className="max-w-xl font-heading text-2xl font-semibold uppercase tracking-wide text-white sm:text-3xl">
            {t("landing.ways_title")}
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { accent: "#4A7C59", title: t("landing.ways_contribute_title"), body: t("landing.ways_contribute_body"), link: "/register?role=authority", linkLabel: t("landing.ways_contribute_link"), tid: "way-contribute" },
              { accent: "#C99B4B", title: t("landing.ways_participate_title"), body: t("landing.ways_participate_body"), link: "/contribute?tab=apply", linkLabel: t("landing.ways_participate_link"), tid: "way-participate" },
              { accent: "#9E4747", title: t("landing.ways_fund_title"), body: t("landing.ways_fund_body"), mailto: "info@bureauvanmeerbeeck.com", linkLabel: t("landing.ways_fund_link"), tid: "way-fund" },
            ].map((w) => (
              <div key={w.tid} className="relative overflow-hidden rounded-lg border border-white/10 bg-white/5 p-7 pl-8 transition-base hover:border-white/25" data-testid={w.tid}>
                <span className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: w.accent }} />
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wide text-white">{w.title}</h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-white/65">{w.body}</p>
                {w.link && (
                  <Link to={w.link} className="mt-5 inline-flex items-center gap-1 font-body text-sm font-medium transition-base hover:opacity-80" style={{ color: w.accent === "#4A7C59" ? "#7FB08E" : "#DDBA78" }} data-testid={`${w.tid}-link`}>
                    {w.linkLabel} <ArrowRight size={14} />
                  </Link>
                )}
                {w.mailto && (
                  <a href={`mailto:${w.mailto}`} className="mt-5 inline-flex items-center gap-1 font-body text-sm font-medium transition-base hover:opacity-80" style={{ color: "#D98585" }} data-testid={`${w.tid}-link`}>
                    {w.linkLabel} <ArrowRight size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0] py-10">
        <div className="mx-auto max-w-6xl px-6 font-body text-xs text-[#5C6873]">
          {t("landing.footer")}
        </div>
      </footer>
    </div>
  );
}
